import { Injectable } from '@angular/core';
import { Subject } from "rxjs";
import { Restaurant } from '../avantoeats-util/models/restaurant/Restaurant';
import { AvantoLambdaService } from './avanto-lambda.service';
import Swal from 'sweetalert2'
import { CartItem } from '../avantoeats-util/models/cart/CartItem';
import { Product } from '../avantoeats-util/models/menu/Product';
import { RestaurantInformation } from '../avantoeats-util/models/session/RestaurantInformation';

@Injectable({
    providedIn: 'root'
})
export class AvantoRestaurantsService {
    /** **************************** SUBJECTS ********************************\
     *  These subjects will be subscribed to our various components, to keep
     * track of any changes made on the data
     * ***********************************************************************/
    readonly subject_selectedRestaurant: Subject<Restaurant> = new Subject();

    readonly subject_selectedRestaurantList: Subject<{
        restaurantList: Restaurant[],
        availabilityMap: Map<string, {
            pickupAvailability: { message: string, isOpen: boolean },
            deliveryAvailability: { message: string, isOpen: boolean }
        }>
    }> = new Subject();

    readonly subject_restaurantAvailability: Subject<
        Map<string, {
            pickupAvailability: { message: string, isOpen: boolean },
            deliveryAvailability: { message: string, isOpen: boolean }
        }>
    > = new Subject();

    readonly subject_cartItem_add_edit_request: Subject<{ product: Product, restaurantId: string, cartItem?: CartItem }> = new Subject();
    /** ******************************************************************** */

    constructor(private lambda: AvantoLambdaService) { }

    /** **************************** Methods ******************************** *\
    *  The following methods are supposed to called from within our components.
    * I guess they would work outside of the components too, so do whatever
    * you want. I don't give a damn.
    * *************************************************************************/

    /**
     * Loads the menu for a restaurant
     * @param restaurantName name of the restaurant which the menu is being loaded
     */
    loadMenu(region: string, clientId: string, custLatitude: number | null, custLongitude: number | null) {
        this.lambda.fetchSingleRestaurant(region, clientId, custLatitude, custLongitude).subscribe((response:any) => {
            const awsResponse = response.json().data;
            const loadedRestaurant = Restaurant.newFromAWS(awsResponse.restaurantInformation, awsResponse.restaurantMenu, awsResponse.serverDateTime);
            this.subject_selectedRestaurant.next(loadedRestaurant);
            const retrievedAvailability = new Map();
            retrievedAvailability.set(loadedRestaurant.getUniqueIdentifier(), {
                pickupAvailability: awsResponse.pickupAvailability,
                deliveryAvailability: awsResponse.deliveryAvailability
            })
            this.subject_restaurantAvailability.next(retrievedAvailability)
        }, (error) => {
            console.error("Could not fetch restaurant...", error);
            Swal.fire("Could not load restaurant menu", "Please try refreshing the page!", "error")
        })
    }

    loadRestaurantList(custLatitude: number | null, custLongitude: number | null) {
        this.lambda.fetchRestaurantList(custLatitude, custLongitude).subscribe((response:any) => {
            const retrievedList: Restaurant[] = [];
            const retrievedAvailability = new Map();
            response.json().data.forEach(restaurant => {
                const newRestaurant = Restaurant.newFromAWS(restaurant.restaurantInformation);
                retrievedList.push(newRestaurant)
                retrievedAvailability.set(
                    newRestaurant.getUniqueIdentifier(), {
                    pickupAvailability: restaurant.pickupAvailability,
                    deliveryAvailability: restaurant.deliveryAvailability
                })
            });

            this.subject_selectedRestaurantList.next({
                restaurantList: retrievedList,
                availabilityMap: retrievedAvailability
            });

        }, (error) => {
            console.error("Could not fetch restaurant list...", error);
            Swal.fire("Could not load restaurant list", "Please try refreshing the page!", 'error')
        });
    }

    refreshRestaurantAvailability(restaurantInformation: RestaurantInformation[], custLatitude: number | null, custLongitude: number | null) {
        this.lambda.refreshRestaurantAvailability(restaurantInformation, custLatitude, custLongitude).subscribe((response) => {
            const retrievedAvailabilityMap = new Map();
            response.json().data.forEach(response => {
                retrievedAvailabilityMap.set(
                    response.restaurantInformation.restaurantId, {
                    pickupAvailability: response.pickupAvailability,
                    deliveryAvailability: response.deliveryAvailability
                })
            });
            this.subject_restaurantAvailability.next(retrievedAvailabilityMap);
        }, (error) => {
            console.error("Could not fetch restaurant list...", error);
            Swal.fire("Could not load restaurant list", "Please try refreshing the page!", "error")
        });
    }

    requestMenuItemEdit(restaurantId: string, requestedCartItem: CartItem) {
        this.lambda.getRestaurantProduct(restaurantId, requestedCartItem.itemId).subscribe((response: any) => {
            const retreivedRestaurantProduct = Product.newFromAWS(JSON.parse(response._body).data);
            this.subject_cartItem_add_edit_request.next({
                product: retreivedRestaurantProduct,
                restaurantId: restaurantId,
                cartItem: requestedCartItem
            });
        }), (error) => {
            //TODO: IF THE PRODUCT NO LONGER EXISTS, SAY SOMETHING
            console.error("Could not retrieve product...", error);
            Swal.fire("Could not load restaurant product.", "It may no longer be on the menu...", "error")
        };
    }

    requestMenuAdd(restaurantId: string, addedProduct: Product) {
        this.subject_cartItem_add_edit_request.next({
            product: addedProduct,
            restaurantId: restaurantId
        });
    }
}